import { useState, useEffect, useCallback } from 'react';
import {
  Calendar, Video, Clock, Loader2, RefreshCw, Link2, Users, MapPin, ExternalLink, AlertCircle,
  Plus, Globe, Trash2, Settings, ToggleLeft, ToggleRight, Unlink, X, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const CalendarView = ({ user }) => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [view, setView] = useState('today'); // 'today' | 'upcoming'
  const [calendarFeeds, setCalendarFeeds] = useState([]);
  const [googleEnabled, setGoogleEnabled] = useState(true);
  const [showAddFeed, setShowAddFeed] = useState(false);
  const [feedName, setFeedName] = useState('');
  const [feedUrl, setFeedUrl] = useState('');
  const [addingFeed, setAddingFeed] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, todayRes, upcomingRes, feedsRes] = await Promise.all([
        authFetch(`${API}/api/calendar/auth/status`),
        authFetch(`${API}/api/calendar/events/today`),
        authFetch(`${API}/api/calendar/events`).catch(() => null),
        authFetch(`${API}/api/calendar/feeds`).catch(() => null),
      ]);

      if (statusRes.ok) {
        const s = await statusRes.json();
        setConnected(s.connected);
      }
      if (todayRes.ok) {
        const t = await todayRes.json();
        setTodayEvents(t.events || []);
        if (t.connected !== undefined) setConnected(t.connected);
      }
      if (upcomingRes?.ok) {
        const u = await upcomingRes.json();
        setEvents(u.events || []);
      }
      if (feedsRes?.ok) {
        const f = await feedsRes.json();
        setCalendarFeeds(f.feeds || []);
        setGoogleEnabled(f.google?.enabled !== false);
      }
    } catch (e) { console.error('Calendar data error:', e); }
    finally { setLoading(false); }
     
  }, []);

  useEffect(() => { fetchCalendarData(); }, [fetchCalendarData]);

  const connectCalendar = async () => {
    try {
      const res = await authFetch(`${API}/api/calendar/auth/start`);
      if (res.ok) {
        const data = await res.json();
        window.open(data.authorization_url, '_blank', 'width=600,height=700');
        toast.info('Complete authorization in the popup window');
      } else {
        toast.error('Failed to start calendar authorization');
      }
    } catch (e) { toast.error('Failed to connect calendar'); }
  };

  const addFeed = async () => {
    if (!feedUrl.trim()) { toast.error('URL is required'); return; }
    setAddingFeed(true);
    try {
      const res = await authFetch(`${API}/api/calendar/feeds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: feedUrl.trim(), name: feedName.trim() || 'Calendar Feed' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to add feed');
      }
      const feed = await res.json();
      toast.success(`Feed added (${feed.event_count} events today)`);
      setFeedName(''); setFeedUrl(''); setShowAddFeed(false);
      fetchCalendarData();
    } catch (e) { toast.error(e.message); }
    finally { setAddingFeed(false); }
  };

  const toggleFeed = async (feedId, enabled) => {
    try {
      await authFetch(`${API}/api/calendar/feeds/${feedId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      setCalendarFeeds(prev => prev.map(f => f.id === feedId ? { ...f, enabled } : f));
      fetchCalendarData();
    } catch (e) { toast.error('Failed to update feed'); }
  };

  const deleteFeed = async (feedId) => {
    if (!window.confirm('Remove this calendar feed?')) return;
    try {
      await authFetch(`${API}/api/calendar/feeds/${feedId}`, { method: 'DELETE' });
      setCalendarFeeds(prev => prev.filter(f => f.id !== feedId));
      fetchCalendarData();
      toast.success('Feed removed');
    } catch (e) { toast.error('Failed to delete feed'); }
  };

  const toggleGoogle = async (enabled) => {
    try {
      await authFetch(`${API}/api/calendar/sources/google`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      setGoogleEnabled(enabled);
      fetchCalendarData();
    } catch (e) { toast.error('Failed to toggle'); }
  };

  const disconnectCalendar = async () => {
    try {
      const res = await authFetch(`${API}/api/calendar/disconnect`, {
        method: 'POST'
      });
      if (res.ok) {
        setConnected(false);
        setEvents([]);
        setTodayEvents([]);
        toast.success('Calendar disconnected');
      }
    } catch (e) { toast.error('Failed to disconnect'); }
  };

  const syncTranscripts = async () => {
    try {
      const res = await authFetch(`${API}/api/calendar/sync-transcripts`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Synced ${data.synced_count} transcripts (checked ${data.checked_count} events)`);
      }
    } catch (e) { toast.error('Transcript sync failed'); }
  };

  const displayEvents = view === 'today' ? todayEvents : events;

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Group upcoming events by date
  const eventsByDate = {};
  displayEvents.forEach(ev => {
    const date = ev.start?.dateTime ? formatDate(ev.start.dateTime) : (ev.start?.date || 'No Date');
    if (!eventsByDate[date]) eventsByDate[date] = [];
    eventsByDate[date].push(ev);
  });

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="calendar-loading">
      <Loader2 className="h-8 w-8 animate-spin text-brand" />
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight text-text-primary" data-testid="calendar-page-title">Calendar</h2>
          <p className="text-text-secondary text-sm mt-1">
            {connected ? `${todayEvents.length} events today · ${events.length} upcoming` : 'Connect Google Calendar to see your schedule'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowSources(!showSources)} data-testid="manage-sources-btn">
            <Settings size={14} className="mr-1.5" /> {showSources ? 'Hide Sources' : 'Manage Sources'}
          </Button>
          {connected && (
            <>
              <Button variant="outline" size="sm" onClick={syncTranscripts} data-testid="sync-transcripts-btn">
                <RefreshCw size={14} className="mr-1.5" /> Sync Transcripts
              </Button>
              <Button variant="outline" size="sm" onClick={fetchCalendarData} data-testid="refresh-calendar">
                <RefreshCw size={14} className="mr-1.5" /> Refresh
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Calendar Sources Panel */}
      {showSources && (
        <Card className="border-border-subtle" data-testid="calendar-sources-panel">
          <CardContent className="py-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">Calendar Sources</p>

            {/* Google Calendar */}
            <div className="flex items-center justify-between p-3 bg-surface-page rounded-lg" data-testid="google-cal-row">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Calendar size={14} className="text-blue-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-text-primary">Google Calendar</p>
                  <p className="text-xs text-text-tertiary">
                    {connected ? 'Connected — auto-syncs transcripts' : 'Not connected'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {connected ? (
                  <>
                    <button onClick={() => toggleGoogle(!googleEnabled)} className="p-1" data-testid="toggle-google">
                      {googleEnabled ? <ToggleRight size={24} className="text-brand" /> : <ToggleLeft size={24} className="text-text-muted" />}
                    </button>
                    <button onClick={disconnectCalendar} className="p-1 text-text-muted hover:text-red-500" data-testid="disconnect-google">
                      <Unlink size={14} />
                    </button>
                  </>
                ) : (
                  <Button size="sm" onClick={connectCalendar} className="bg-brand hover:bg-brand-hover" data-testid="connect-google">
                    <Link2 size={14} className="mr-1" /> Connect
                  </Button>
                )}
              </div>
            </div>

            {/* ICS Feeds */}
            {calendarFeeds.map(feed => (
              <div key={feed.id} className="flex items-center justify-between p-3 bg-surface-page rounded-lg" data-testid={`feed-row-${feed.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                    <Globe size={14} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">{feed.name}</p>
                    <p className="text-xs text-text-tertiary truncate max-w-[300px]">{feed.url}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleFeed(feed.id, !feed.enabled)} className="p-1">
                    {feed.enabled ? <ToggleRight size={24} className="text-brand" /> : <ToggleLeft size={24} className="text-text-muted" />}
                  </button>
                  <button onClick={() => deleteFeed(feed.id)} className="p-1 text-text-muted hover:text-red-500">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}

            {/* Add Feed */}
            {showAddFeed ? (
              <div className="p-3 border border-dashed border-border-subtle rounded-lg space-y-2" data-testid="add-feed-form">
                <Input placeholder="Feed name (e.g., BCM Shared Calendar)" value={feedName} onChange={e => setFeedName(e.target.value)} data-testid="feed-name-input" />
                <Input placeholder="ICS/iCal URL (https://...)" value={feedUrl} onChange={e => setFeedUrl(e.target.value)} data-testid="feed-url-input" />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setShowAddFeed(false); setFeedName(''); setFeedUrl(''); }}>Cancel</Button>
                  <Button size="sm" onClick={addFeed} disabled={addingFeed} className="bg-brand hover:bg-brand-hover" data-testid="save-feed-btn">
                    {addingFeed ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />}
                    Add Feed
                  </Button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowAddFeed(true)} className="w-full p-3 border border-dashed border-border-subtle rounded-lg text-sm text-text-muted hover:text-brand hover:border-brand/30 transition-colors flex items-center justify-center gap-2" data-testid="add-feed-btn">
                <Plus size={14} /> Add Calendar Feed (ICS/iCal URL)
              </button>
            )}
          </CardContent>
        </Card>
      )}

      {/* No sources at all */}
      {!connected && calendarFeeds.length === 0 && !showSources ? (
        <div className="max-w-md mx-auto text-center py-16">
          <Calendar size={48} className="mx-auto text-text-muted mb-4" />
          <h3 className="text-xl font-semibold text-text-primary mb-2">Connect a Calendar</h3>
          <p className="text-text-secondary text-sm mb-6">
            Connect Google Calendar or add an ICS/iCal feed URL to see your meetings.
          </p>
          <div className="flex gap-3 justify-center">
            <Button className="bg-brand hover:bg-brand-hover text-white" onClick={connectCalendar} data-testid="connect-calendar-cta">
              <Link2 size={14} className="mr-1.5" /> Google Calendar
            </Button>
            <Button variant="outline" onClick={() => { setShowSources(true); setShowAddFeed(true); }} data-testid="add-ics-cta">
              <Globe size={14} className="mr-1.5" /> Add ICS Feed
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* View Toggle */}
          <div className="flex items-center gap-1 p-1 bg-surface-page rounded-xl w-fit" data-testid="calendar-view-toggle">
            <button
              onClick={() => setView('today')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                view === 'today' ? 'bg-white text-brand shadow-xs' : 'text-text-secondary hover:text-text-primary'
              }`}
              data-testid="view-today"
            >
              Today ({todayEvents.length})
            </button>
            <button
              onClick={() => setView('upcoming')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                view === 'upcoming' ? 'bg-white text-brand shadow-xs' : 'text-text-secondary hover:text-text-primary'
              }`}
              data-testid="view-upcoming"
            >
              Upcoming ({events.length})
            </button>
          </div>

          {/* Events */}
          {displayEvents.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border-subtle rounded-xl">
              <Calendar size={40} className="mx-auto text-text-muted mb-3" />
              <p className="text-text-secondary font-medium">
                {view === 'today' ? 'No meetings scheduled for today' : 'No upcoming events'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(eventsByDate).map(([date, dateEvents]) => (
                <div key={date} data-testid={`date-group-${date}`}>
                  <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">{date}</h3>
                  <div className="space-y-2">
                    {dateEvents.map((ev, idx) => {
                      const startTime = ev.start?.dateTime ? new Date(ev.start.dateTime) : null;
                      const endTime = ev.end?.dateTime ? new Date(ev.end.dateTime) : null;
                      const hasGoogleMeet = !!(ev.hangoutLink || ev.conferenceData);
                      const isIcsFeed = ev.source === 'ics_feed';
                      const now = new Date();
                      const isOngoing = startTime && endTime && now >= startTime && now <= endTime;
                      const isPast = endTime && now > endTime;
                      const attendeeCount = (ev.attendees || []).length;
                      const location = ev.location;

                      return (
                        <div
                          key={ev.id || idx}
                          onClick={() => setSelectedEvent(ev)}
                          className={`p-4 rounded-xl border transition-all cursor-pointer ${
                            isOngoing
                              ? 'bg-success/5 border-success/20 shadow-xs'
                              : isPast
                              ? 'bg-surface-page border-border-subtle opacity-60'
                              : 'bg-white border-border-subtle hover:border-brand/20 hover:shadow-xs'
                          }`}
                          data-testid={`event-${ev.id || idx}`}
                        >
                          <div className="flex items-start gap-4">
                            {/* Time Column */}
                            <div className="w-20 flex-shrink-0 text-right pt-0.5">
                              <p className={`text-sm font-semibold ${isOngoing ? 'text-success' : 'text-text-primary'}`}>
                                {formatTime(ev.start?.dateTime)}
                              </p>
                              {endTime && (
                                <p className="text-xs text-text-tertiary">{formatTime(ev.end?.dateTime)}</p>
                              )}
                            </div>

                            {/* Divider */}
                            <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${
                              isOngoing ? 'bg-success' : isPast ? 'bg-border-subtle' : 'bg-brand/30'
                            }`} />

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-text-primary truncate">{ev.summary || 'No Title'}</h4>
                                {isOngoing && (
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-success bg-success/10 px-2 py-0.5 rounded-full animate-pulse">
                                    Live
                                  </span>
                                )}
                                {isPast && (
                                  <span className="text-[10px] font-semibold uppercase text-text-muted bg-surface-page px-2 py-0.5 rounded-full">
                                    Done
                                  </span>
                                )}
                                {hasGoogleMeet && (
                                  <Video size={14} className="text-info flex-shrink-0" />
                                )}
                                {isIcsFeed && ev.source_name && (
                                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                    {ev.source_name}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center flex-wrap gap-3 mt-1.5 text-xs text-text-tertiary">
                                {startTime && endTime && (
                                  <span className="flex items-center gap-1">
                                    <Clock size={11} />
                                    {Math.round((endTime - startTime) / 60000)} min
                                  </span>
                                )}
                                {attendeeCount > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Users size={11} />
                                    {attendeeCount} attendee{attendeeCount > 1 ? 's' : ''}
                                  </span>
                                )}
                                {location && (
                                  <span className="flex items-center gap-1 truncate max-w-[200px]">
                                    <MapPin size={11} />
                                    {location}
                                  </span>
                                )}
                                {ev.organizer?.email && (
                                  <span className="text-text-muted truncate max-w-[180px]">
                                    by {ev.organizer.displayName || ev.organizer.email}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {hasGoogleMeet && !isPast && (
                                <a
                                  href={ev.hangoutLink || ev.conferenceData?.entryPoints?.[0]?.uri}
                                  target="_blank" rel="noopener noreferrer"
                                  className={`text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors ${
                                    isOngoing
                                      ? 'bg-success text-white hover:bg-success/90'
                                      : 'bg-info/10 text-info hover:bg-info/20'
                                  }`}
                                  data-testid={`join-btn-${ev.id || idx}`}
                                >
                                  <Video size={12} /> Join
                                </a>
                              )}
                              {ev.htmlLink && (
                                <a
                                  href={ev.htmlLink}
                                  target="_blank" rel="noopener noreferrer"
                                  className="text-text-muted hover:text-brand transition-colors p-1.5 rounded-lg hover:bg-brand/5"
                                  title="Open in Google Calendar"
                                >
                                  <ExternalLink size={14} />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Event Detail Panel */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setSelectedEvent(null)} data-testid="event-detail-overlay">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="event-detail-panel">
            {/* Header */}
            <div className="p-5 border-b border-border-subtle">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-text-primary" data-testid="event-detail-title">
                    {selectedEvent.summary || 'No Title'}
                  </h3>
                  {selectedEvent.source_name && (
                    <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded mt-1 inline-block">
                      {selectedEvent.source_name}
                    </span>
                  )}
                </div>
                <button onClick={() => setSelectedEvent(null)} className="p-1 hover:bg-surface-page rounded-lg" data-testid="close-event-detail">
                  <X size={18} className="text-text-muted" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              {/* Time */}
              {selectedEvent.start?.dateTime && (
                <div className="flex items-center gap-3" data-testid="event-time">
                  <Clock size={16} className="text-text-muted flex-shrink-0" />
                  <div>
                    <p className="text-sm text-text-primary">
                      {new Date(selectedEvent.start.dateTime).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-sm text-text-secondary">
                      {formatTime(selectedEvent.start.dateTime)}
                      {selectedEvent.end?.dateTime && ` — ${formatTime(selectedEvent.end.dateTime)}`}
                      {selectedEvent.end?.dateTime && selectedEvent.start?.dateTime && (
                        <span className="text-text-tertiary ml-1">
                          ({Math.round((new Date(selectedEvent.end.dateTime) - new Date(selectedEvent.start.dateTime)) / 60000)} min)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}
              {selectedEvent.start?.date && !selectedEvent.start?.dateTime && (
                <div className="flex items-center gap-3">
                  <Clock size={16} className="text-text-muted flex-shrink-0" />
                  <p className="text-sm text-text-primary">All day — {selectedEvent.start.date}</p>
                </div>
              )}

              {/* Location */}
              {selectedEvent.location && (
                <div className="flex items-start gap-3" data-testid="event-location">
                  <MapPin size={16} className="text-text-muted flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-text-primary">{selectedEvent.location}</p>
                </div>
              )}

              {/* Conference Link */}
              {(selectedEvent.hangoutLink || selectedEvent.conferenceData?.entryPoints?.[0]?.uri) && (
                <div className="flex items-center gap-3" data-testid="event-conference">
                  <Video size={16} className="text-blue-500 flex-shrink-0" />
                  <a
                    href={selectedEvent.hangoutLink || selectedEvent.conferenceData?.entryPoints?.[0]?.uri}
                    target="_blank" rel="noopener noreferrer"
                    className="text-sm text-brand hover:underline"
                  >
                    Join video meeting
                  </a>
                </div>
              )}

              {/* Organizer */}
              {selectedEvent.organizer?.email && (
                <div className="flex items-center gap-3" data-testid="event-organizer">
                  <Users size={16} className="text-text-muted flex-shrink-0" />
                  <p className="text-sm text-text-secondary">
                    Organized by <span className="text-text-primary">{selectedEvent.organizer.displayName || selectedEvent.organizer.email}</span>
                  </p>
                </div>
              )}

              {/* Attendees */}
              {selectedEvent.attendees?.length > 0 && (
                <div data-testid="event-attendees">
                  <p className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
                    Attendees ({selectedEvent.attendees.length})
                  </p>
                  <div className="space-y-1">
                    {selectedEvent.attendees.map((att, i) => (
                      <div key={att.email || i} className="flex items-center gap-2 text-sm">
                        <div className="w-6 h-6 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-brand">
                            {(att.displayName || att.email || '?')[0].toUpperCase()}
                          </span>
                        </div>
                        <span className="text-text-primary truncate">{att.displayName || att.email}</span>
                        {att.responseStatus === 'accepted' && <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />}
                        {att.responseStatus === 'declined' && <AlertCircle size={12} className="text-red-400 flex-shrink-0" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedEvent.description && (
                <div data-testid="event-description">
                  <p className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">Description</p>
                  <div className="text-sm text-text-secondary bg-surface-page rounded-lg p-3 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                    {selectedEvent.description}
                  </div>
                </div>
              )}

              {/* Open in Google Calendar */}
              {selectedEvent.htmlLink && (
                <a
                  href={selectedEvent.htmlLink}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-brand hover:underline mt-2"
                  data-testid="open-in-gcal"
                >
                  <ExternalLink size={14} /> Open in Google Calendar
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
